<template>
  <div>
    <div v-if="isLoading" class="loading-container">
      <img :src="require('@/assets/10.png')" alt="Logo" class="loading-logo" />
    </div>
    <div v-else>
      <div class="slide">
        <div class="bg-img">
          <div class="slide-content">
            <div>
              <h2 class="slide-header">{{ $t('message.allEquipmentFamilies') }}</h2>
            </div>
            <div>
              <h5 class="slide-h5">{{ $t('message.home') }}</h5>
              <b-icon :icon="isRTL ? 'chevron-double-left' : 'chevron-double-right'" class="icon-slider"></b-icon>
              <h5 class="slide-h5">{{ $t('message.allEquipmentFamilies') }}</h5>
            </div>
          </div>
        </div>
      </div>

      <div class="container custom-container-families mt-5">
        <b-row class="mb-4">
          <b-col cols="12" md="8">
            <div class="search-box">
              <b-icon icon="search" class="search-icon"></b-icon>
              <b-form-input
                v-model="searchQuery"
                :placeholder="$t('message.search')"
                class="search-input"
              ></b-form-input>
            </div>
          </b-col>
          <b-col cols="12" md="4" class="text-right">
            <b-button variant="warning" @click="goBack" class="back-button">
              <b-icon icon="arrow-left" class="mr-1"></b-icon>
              {{ $t('message.back') }}
            </b-button>
          </b-col>
        </b-row>

        <b-row>
          <b-col v-for="family in filteredFamilies" :key="family.id" cols="12" md="6" lg="4" class="mb-4">
            <div class="family-card">
              <div class="card-header-custom">
                <div class="header-content">
                  <b-icon icon="collection" class="header-icon"></b-icon>
                  <div class="header-text">
                    <h5 class="family-name">{{ family.name }}</h5>
                    <span class="family-code-badge">{{ family.family_code }}</span>
                  </div>
                </div>
                <b-button variant="danger" size="sm" class="delete-btn" @click="confirmDelete(family)">
                  <b-icon icon="trash-fill"></b-icon>
                </b-button>
              </div>
              <div class="card-body-custom">
                <div class="info-item">
                  <div class="info-label">
                    <b-icon icon="tag-fill" class="info-icon"></b-icon>
                    {{ $t('message.equipmentFamily') }}
                  </div>
                  <div class="info-value">{{ family.name }}</div>
                </div>
                <div class="info-item">
                  <div class="info-label">
                    <b-icon icon="upc-scan" class="info-icon"></b-icon>
                    {{ $t('message.familyCode') }}
                  </div>
                  <div class="info-value highlight">{{ family.family_code }}</div>
                </div>
                <div v-if="family.type_code" class="info-item">
                  <div class="info-label">
                    <b-icon icon="code-square" class="info-icon"></b-icon>
                    {{ $t('message.activityDomain') }}
                  </div>
                  <div class="info-value type-code">{{ family.type_code }}</div>
                </div>
              </div>
              <div class="card-footer-custom">
                <div class="card-wave"></div>
              </div>
            </div>
          </b-col>

          <template v-if="filteredFamilies.length === 0">
            <div class="text-center p-4" style="width: 100%;">
              <img :src="require('../assets/not-found.svg')" alt="Not Found" style="width:50%; max-width: 400px;">
              <p class="mb-0 filter-result">{{ $t('message.noMatchingResults') }} "{{ searchQuery }}".</p>
            </div>
          </template>
        </b-row>

        <div class="mt-5 pagination">
          <b-pagination v-model="currentPage" pills :total-rows="rows" :per-page="perPage"></b-pagination>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <b-modal
      id="delete-modal"
      v-model="showDeleteModal"
      :title="$t('message.deleteConfirmation')"
      @ok="deleteFamily"
      ok-variant="danger"
      :ok-title="$t('message.block')"
      :cancel-title="$t('message.close')"
    >
      <p>{{ $t('message.deleteConfirmation') }}</p>
    </b-modal>
  </div>
</template>

<script>
import axiosInstance from '../axios-config';
import { mapGetters } from 'vuex';

export default {
  name: 'EquipmentFamilies',
  data() {
    return {
      families: [],
      searchQuery: '',
      isLoading: true,
      currentPage: 1,
      perPage: 12,
      rows: 0,
      showDeleteModal: false,
      selectedFamily: null,
    };
  },
  computed: {
    ...mapGetters(['getUser']),
    company_id() {
      return this.getUser;
    },
    isRTL() {
      return this.$i18n.locale === 'ar';
    },
    filteredFamilies() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      
      let filtered = this.families;
      
      if (this.searchQuery) {
        filtered = this.families.filter(family =>
          family.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          family.family_code.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          (family.type_code && family.type_code.toLowerCase().includes(this.searchQuery.toLowerCase()))
        );
      }
      
      this.rows = filtered.length;
      return filtered.slice(start, end);
    },
  },
  created() {
    this.fetchFamilies();
  },
  methods: {
    async fetchFamilies() {
      try {
        const formData = new FormData();
        formData.append('company_id', this.company_id.company_id);
        
        const response = await axiosInstance.post('company/select_equipments_family.php', formData);
        
        if (response.data.status === "true") {
          this.families = response.data.output;
          this.rows = this.families.length;
        } else {
          this.families = [];
        }
        
        this.isLoading = false;
      } catch (error) {
        console.error('Error fetching families:', error);
        this.$izitoast.error({
          title: this.$t('message.error'),
          message: this.$t('message.errorGettingData'),
          position: 'bottomLeft',
          timeout: 5000,
        });
        this.isLoading = false;
      }
    },
    
    confirmDelete(family) {
      this.selectedFamily = family;
      this.showDeleteModal = true;
    },
    
    async deleteFamily() {
      if (!this.selectedFamily) return;
      
      try {
        const formData = new FormData();
        formData.append('family_id', this.selectedFamily.id);
        
        const response = await axiosInstance.post('company/delete_equipment_family.php', formData);
        
        if (response.data.status === "true") {
          this.$izitoast.success({
            title: this.$t('message.success'),
            message: this.$t('message.familyDeletedSuccessfully'),
            position: 'bottomLeft',
            timeout: 3000,
          });
          
          // Refresh the list
          this.fetchFamilies();
        } else {
          this.$izitoast.error({
            title: this.$t('message.error'),
            message: this.$t('message.errorDeletingFamily'),
            position: 'bottomLeft',
            timeout: 5000,
          });
        }
      } catch (error) {
        console.error('Error deleting family:', error);
        this.$izitoast.error({
          title: this.$t('message.error'),
          message: this.$t('message.errorDeletingFamily'),
          position: 'bottomLeft',
          timeout: 5000,
        });
      }
      
      this.selectedFamily = null;
      this.showDeleteModal = false;
    },
    
    goBack() {
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped>
.custom-container-families {
  padding-left: 100px;
  padding-right: 100px;
}

/* Search Box Styling */
.search-box {
  position: relative;
  width: 100%;
}

.search-input {
  padding-left: 50px;
  padding-right: 20px;
  height: 55px;
  border-radius: 30px;
  border: 3px solid #E7E7E7;
  font-family: 'Somar-Medium';
  font-size: 17px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.search-input:focus {
  border-color: #FFD448;
  box-shadow: 0 0 0 4px rgba(255, 212, 72, 0.2), 0 4px 20px rgba(255, 212, 72, 0.3);
  transform: translateY(-2px);
}

.search-icon {
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: #FFD448;
  font-size: 22px;
  z-index: 10;
}

/* Back Button Styling */
.back-button {
  background: linear-gradient(135deg, #FFCA1F 0%, #FFD44A 100%);
  border-radius: 25px;
  color: #fff;
  font-size: 18px;
  font-family: 'Somar-bold';
  border: none;
  padding: 12px 30px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(255, 202, 31, 0.3);
}

.back-button:hover {
  background: linear-gradient(135deg, #FFD44A 0%, #FFCA1F 100%);
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(255, 202, 31, 0.5);
}

/* Family Card - Modern Glass Morphism Design */
.family-card {
  position: relative;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 2px solid rgba(255, 212, 72, 0.2);
  height: 100%;
}

.family-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 212, 72, 0.3), transparent);
  transition: left 0.5s ease;
}

.family-card:hover::before {
  left: 100%;
}

.family-card:hover {
  transform: translateY(-10px) scale(1.02);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  border-color: #FFD448;
}

/* Card Header */
.card-header-custom {
  background: linear-gradient(135deg, #2274BD 0%, #3C89CE 100%);
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.card-header-custom::after {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  animation: shimmer 3s ease-in-out infinite;
}

@keyframes shimmer {
  0%, 100% {
    transform: translate(0, 0);
  }
  50% {
    transform: translate(-10px, -10px);
  }
}

.header-content {
  display: flex;
  align-items: center;
  gap: 15px;
  flex: 1;
}

.header-icon {
  font-size: 32px;
  color: #FFD448;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.header-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.family-name {
  margin: 0;
  color: #fff;
  font-family: 'Somar-Bold';
  font-size: 18px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.family-code-badge {
  display: inline-block;
  background: linear-gradient(135deg, #FFD448 0%, #FFCA1F 100%);
  color: #2274BD;
  padding: 6px 15px;
  border-radius: 20px;
  font-family: 'Somar-Bold';
  font-size: 13px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
}

.delete-btn {
  background: linear-gradient(135deg, #ff4757 0%, #ff3838 100%);
  border: none;
  padding: 10px 12px;
  border-radius: 12px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(255, 71, 87, 0.3);
  z-index: 10;
}

.delete-btn:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(255, 71, 87, 0.5);
}

/* Card Body */
.card-body-custom {
  padding: 25px 20px;
  background: #fff;
}

.info-item {
  margin-bottom: 18px;
  padding: 15px;
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
  border-radius: 12px;
  border-left: 4px solid #FFD448;
  transition: all 0.3s ease;
}

.info-item:hover {
  transform: translateX(5px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-left-color: #2274BD;
}

.info-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #2B5FA8;
  font-family: 'Somar-Bold';
  font-size: 14px;
  margin-bottom: 8px;
}

.info-icon {
  font-size: 18px;
  color: #FFD448;
}

.info-value {
  color: #495057;
  font-family: 'Somar-Medium';
  font-size: 15px;
  padding-left: 26px;
}

.info-value.highlight {
  background: linear-gradient(135deg, #FFD448 0%, #FFCA1F 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-family: 'Somar-Bold';
  font-size: 16px;
}

.info-value.type-code {
  color: #3C89CE;
  font-family: 'Somar-Bold';
}

/* Card Footer Wave Effect */
.card-footer-custom {
  position: relative;
  height: 8px;
  background: linear-gradient(90deg, #2274BD 0%, #3C89CE 50%, #FFD448 100%);
  overflow: hidden;
}

.card-wave {
  position: absolute;
  width: 200%;
  height: 100%;
  background: linear-gradient(90deg, 
    transparent 0%, 
    rgba(255, 255, 255, 0.3) 50%, 
    transparent 100%
  );
  animation: wave 3s linear infinite;
}

@keyframes wave {
  0% {
    transform: translateX(-50%);
  }
  100% {
    transform: translateX(0%);
  }
}

/* Pagination */
.pagination {
  justify-content: center;
  display: flex;
  align-items: center;
}

/* Loading */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.loading-logo {
  animation: puls 4s ease-out alternate infinite;
  height: 110px;
  filter: drop-shadow(0 4px 20px rgba(34, 116, 189, 0.3));
}

@keyframes puls {
  0% {
    transform: scale(0.8) rotate(0deg);
  }
  100% {
    transform: scale(1.2) rotate(5deg);
  }
}

/* No Results */
.filter-result {
  color: #777D85;
  font-family: 'Somar-Bold';
  font-size: 22px;
  margin-top: 20px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Responsive */
@media (max-width: 768px) {
  .custom-container-families {
    padding-left: 15px;
    padding-right: 15px;
  }
  
  .back-button {
    width: 100%;
    margin-top: 15px;
  }
  
  .family-name {
    font-size: 16px;
  }
  
  .header-icon {
    font-size: 28px;
  }
}
</style>

